/**
 * RequestDetails — Workflow, status transitions, escalate, cancel, follow-up.
 * Mixed into RequestDetails.prototype.
 */
import { apiClient } from '../../utils/ApiClient.js';
import { showToast } from '../../utils/toast.js';

export const WorkflowMixin = {

    // ── Pipeline / breadcrumbs ───────────────────────────────────────────────

    syncWorkflowButtonState() {
        const root = this.details?.querySelector('.request-step-panels');
        if (!root) return;
        const server     = root.getAttribute('data-server-status') || '';
        const active     = root.querySelector('.request-step-panel.request-step-panel--active');
        const activeStep = active?.getAttribute('data-step-status') || '';
        const allowed    = activeStep === server && server !== '';
        root.querySelectorAll('form[data-workflow-step] button[data-workflow-action]').forEach((b) => {
            const action = b.getAttribute('data-workflow-action') || '';
            if (action === 'generate_invoice' && b.id === 'generate_invoice_btn') {
                b.disabled = false;
                return;
            }
            b.disabled = !allowed;
        });
    },

    activateRequestStepPanel(status) {
        if (!this.details || !status) return;
        this.details.querySelectorAll('.request-step-panel').forEach((panel) => {
            panel.classList.toggle('request-step-panel--active', panel.getAttribute('data-step-status') === status);
        });
        this.syncWorkflowButtonState();
    },

    applyRequestMetrics(metrics) {
        if (!this.details || !metrics || typeof metrics !== 'object') return;
        const root = this.details.querySelector('.request_additional_container');
        if (!root) return;
        [
            ['follow_up_total', 'follow_up_total'],
            ['follow_up_last', 'follow_up_last'],
            ['days_since_follow_up', 'days_since_follow_up'],
            ['days_in_current_status', 'days_in_current_status'],
            ['request_submitted_date', 'request_submitted_date'],
            ['request_resubmitted_date', 'request_resubmitted_date'],
        ].forEach(([attr, key]) => {
            const el = root.querySelector(`[data-request-metric="${attr}"]`);
            if (!el) return;
            const v = metrics[key];
            el.textContent = (v === null || v === undefined || v === '') ? '—' : String(v);
        });

        // Also update editable date inputs (roi_specialist / admin view).
        ['request_submitted_date', 'request_resubmitted_date'].forEach(key => {
            const input = root.querySelector(`input[name="${key}"]`);
            if (!input) return;
            const v = metrics[key];
            if (v !== null && v !== undefined) input.value = String(v);
        });
    },

    syncPipelineFromResponse(data) {
        if (!this.details) return;
        const wrap = this.details.querySelector('.request_details_wrapper');
        if (data.breadcrumbs_html) {
            const ul = this.details.querySelector('#breadcrumbs-one');
            if (ul) ul.innerHTML = data.breadcrumbs_html;
        }
        if (typeof data.pipeline_step !== 'undefined' && wrap) {
            wrap.setAttribute('data-pipeline-step', String(data.pipeline_step));
        }
        const panelsRoot = this.details.querySelector('.request-step-panels');
        if (panelsRoot && typeof data.current_status === 'string' && data.current_status !== '') {
            panelsRoot.setAttribute('data-server-status', data.current_status);
            this.activateRequestStepPanel(data.current_status);
        } else {
            this.syncWorkflowButtonState();
        }
        if (data.request_metrics) {
            this.applyRequestMetrics(data.request_metrics);
        }
    },

    // ── Workflow form submission ──────────────────────────────────────────────

    async submitWorkflowForm(form, workflowAction, triggerBtn = null, logStatusNote = '') {
        if (!this.activeRequestId || !this.ajaxUrl) return;
        this.setBtnLoading(triggerBtn, true);

        const formData = new URLSearchParams();
        formData.append('action', 'save_request_workflow');
        formData.append('workflow_action', workflowAction);
        formData.append('request_id', String(this.activeRequestId));
        formData.append('workflow_step_key', form.querySelector('[name="workflow_step_key"]')?.value || form.getAttribute('data-workflow-step') || '');
        formData.append('request_workflow_nonce', form.querySelector('[name="request_workflow_nonce"]')?.value || '');
        form.querySelectorAll('input[type="checkbox"][name="workflow_checked[]"]:checked').forEach((cb) => {
            formData.append('workflow_checked[]', cb.value);
        });
        if (logStatusNote) formData.append('log_status_note', logStatusNote);

        try {
            const res  = await fetch(this.ajaxUrl, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: formData.toString() });
            const json = await res.json();
            if (!json.success) {
                this.setBtnLoading(triggerBtn, false);
                this.openWorkflowNoticeModal(json.data?.message || 'Could not save workflow.');
                return;
            }
            this.setBtnLoading(triggerBtn, false);
            this.refreshActivityLists(json.data || {});
            this.syncPipelineFromResponse(json.data || {});
            this.showToast(json.data?.message || 'Saved.');
        } catch (err) {
            console.error(err);
            this.setBtnLoading(triggerBtn, false);
            this.showToast('Could not save workflow.', 'error');
        }
    },

    async submitWorkflowFormFromModal(form, workflowAction, note, confirmBtn, errorEl) {
        if (!this.activeRequestId || !this.ajaxUrl || !form) {
            this.setBtnLoading(confirmBtn, false);
            return;
        }
        const formData = new URLSearchParams();
        formData.append('action', 'save_request_workflow');
        formData.append('workflow_action', workflowAction);
        formData.append('request_id', String(this.activeRequestId));
        formData.append('workflow_step_key', form.querySelector('[name="workflow_step_key"]')?.value || form.getAttribute('data-workflow-step') || '');
        formData.append('request_workflow_nonce', form.querySelector('[name="request_workflow_nonce"]')?.value || '');
        form.querySelectorAll('input[type="checkbox"][name="workflow_checked[]"]:checked').forEach((cb) => {
            formData.append('workflow_checked[]', cb.value);
        });
        if (note) formData.append('log_status_note', note);
        try {
            const res  = await fetch(this.ajaxUrl, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: formData.toString() });
            const json = await res.json();
            this.setBtnLoading(confirmBtn, false);
            if (!json.success) {
                if (errorEl) { errorEl.textContent = json.data?.message || 'Could not log status.'; errorEl.style.display = 'block'; }
                else window.alert(json.data?.message || 'Could not log status.');
                return;
            }
            this.closeLogStatusModal();
            this.refreshActivityLists(json.data || {});
            this.syncPipelineFromResponse(json.data || {});
            this.showToast(json.data?.message || 'Status logged successfully.');
        } catch (err) {
            console.error(err);
            this.setBtnLoading(confirmBtn, false);
            if (errorEl) { errorEl.textContent = 'An unexpected error occurred.'; errorEl.style.display = 'block'; }
        }
    },

    async submitGenerateRequestDocuments() {
        const modal      = document.getElementById('generate_request_documents_modal');
        const confirmBtn = document.getElementById('generate_request_documents_confirm');
        const rid        = this.activeRequestId ? String(this.activeRequestId) : null;
        if (!rid) return;

        // Collect the templates the user checked (preserving DOM order).
        const errorEl  = modal?.querySelector('#generate_docs_error');
        const selected = [];
        modal?.querySelectorAll('.generate-docs-checkbox:checked').forEach(cb => {
            selected.push(cb.value);
        });

        // If there are checkboxes at all, require at least one to be selected.
        const hasCheckboxes = !!modal?.querySelector('.generate-docs-checkbox');
        if (hasCheckboxes && selected.length === 0) {
            if (errorEl) errorEl.hidden = false;
            return;
        }
        if (errorEl) errorEl.hidden = true;

        this.setModalLoading(modal, true);
        this.setBtnLoading(confirmBtn, true);

        try {
            const data = { request_id: rid };
            if (selected.length) {
                // Send as a JSON string — ApiClient JSON-encodes plain arrays under a
                // single key, which PHP can't parse into an array otherwise.
                data.selected_templates = JSON.stringify(selected);
            }
            const json = await apiClient.post('docuswift_manual_generate_documents', data);
            if (!json.success) {
                this.showToast(json.data?.message || 'Could not generate documents.', 'error');
                return;
            }
            this.closeGenerateRequestDocumentsModal();
            this.showToast(json.data?.message || 'Documents generated successfully.');
            this.fetchDetails(rid);
        } catch (err) {
            console.error(err);
            this.showToast('An unexpected error occurred.', 'error');
        } finally {
            this.setModalLoading(modal, false);
            this.setBtnLoading(confirmBtn, false);
        }
    },

    // ── Cancel request ───────────────────────────────────────────────────────

    cancelRequest(requestId, reason, note = '', confirmBtn = null) {
        const nonce = window.docuswiftPortal?.nonce || '';
        const formData = new URLSearchParams();
        formData.append('action', 'cancel_request');
        formData.append('request_id', requestId);
        formData.append('cancel_reason', reason);
        formData.append('cancel_note', note);
        formData.append('nonce', nonce);

        fetch(this.ajaxUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: formData.toString(),
        })
            .then((res) => res.json())
            .then((res) => {
                if (confirmBtn) this.setBtnLoading(confirmBtn, false);
                this.closeCancelRequestModal();
                if (res.success) {
                    if (res.data?.type === 'pending') {
                        const cancelBtn = document.getElementById('csancel_request_btn');
                        if (cancelBtn) {
                            const infoBox = document.createElement('div');
                            infoBox.id = 'csancel_pending_info';
                            infoBox.className = 'requester_item ds-skeleton active';
                            infoBox.innerHTML = `<div class="namecancel-pending-infobox" style="color:#f19f28!important;display:flex;align-items:center;gap:6px;"><svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0"/><path d="M12 8l0 4"/><path d="M12 16l.01 0"/></svg>Cancellation Requested</div>`;
                            cancelBtn.replaceWith(infoBox);
                        }
                        this.refreshActivityLists(res.data || {});
                        this.showToast(res.data.message || 'Cancellation request submitted.');
                    } else {
                        this.hideCancelBtn();
                        this.refreshActivityLists(res.data || {});
                        this.syncPipelineFromResponse(res.data || {});
                        this.showToast('Request has been canceled successfully.');
                    }
                } else {
                    this.showToast(res.data?.message || 'Could not cancel request.', 'error');
                }
            })
            .catch((err) => {
                if (confirmBtn) this.setBtnLoading(confirmBtn, false);
                this.closeCancelRequestModal();
                console.error('Cancel request error:', err);
                this.showToast('An unexpected error occurred.', 'error');
            });
    },

    resolveCancelRequest(requestId, cancelIndex, resolution, triggerBtn, resolutionNote = '') {
        this.setBtnLoading(triggerBtn, true);
        const nonce = window.docuswiftPortal?.nonce || '';
        const formData = new URLSearchParams();
        formData.append('action', 'resolve_cancel_request');
        formData.append('request_id', requestId);
        formData.append('cancel_index', cancelIndex);
        formData.append('resolution', resolution);
        formData.append('resolution_note', resolutionNote);
        formData.append('nonce', nonce);

        fetch(this.ajaxUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: formData.toString(),
        })
            .then((res) => res.json())
            .then((res) => {
                this.setBtnLoading(triggerBtn, false);
                if (res.success) {
                    this.closeResolveCancelModal();
                    const block = document.getElementById('cancel-request-pending-block');
                    if (block) block.remove();
                    if (res.data?.type === 'accepted') {
                        this.hideCancelBtn();
                        this.refreshActivityLists(res.data || {});
                        this.syncPipelineFromResponse(res.data || {});
                        this.showToast('Cancellation accepted. Request has been canceled.');
                    } else if (res.data?.type === 'invoice') {
                        this.refreshActivityLists(res.data || {});
                        this.syncPipelineFromResponse(res.data || {});
                        // Update sub_status select to 'cancelled'
                        const subStatusEl = this.details?.querySelector('#sub_status');
                        if (subStatusEl) {
                            subStatusEl.value = 'cancelled';
                            subStatusEl.dispatchEvent(new Event('change', { bubbles: true }));
                        }
                        if (res.data?.request_metrics) this.applyRequestMetrics(res.data.request_metrics);
                        this.showToast(res.data?.message || 'Cancellation declined. Invoice to be generated.');
                    } else {
                        this.refreshActivityLists(res.data || {});
                        this.showToast('Cancellation request declined.');
                    }
                } else {
                    const errorEl = document.getElementById('resolve_cancel_modal_error');
                    if (errorEl) { errorEl.textContent = res.data?.message || 'Could not resolve.'; errorEl.style.display = 'block'; }
                    else this.showToast(res.data?.message || 'Could not resolve cancellation request.', 'error');
                }
            })
            .catch((err) => {
                this.setBtnLoading(triggerBtn, false);
                console.error('Resolve cancel error:', err);
                this.showToast('An unexpected error occurred.', 'error');
            });
    },

    // ── Escalate ─────────────────────────────────────────────────────────────

    async escalateRequest(requestId, note = '') {
        const modal      = document.getElementById('escalate_request_modal');
        const confirmBtn = document.getElementById('escalate_request_confirm');
        this.setModalLoading(modal, true);
        this.setBtnLoading(confirmBtn, true);
        try {
            const json = await apiClient.post('docuswift_escalate_request', {
                request_id: String(requestId),
                note,
            });
            if (!json.success) {
                this.setModalLoading(modal, false);
                this.setBtnLoading(confirmBtn, false);
                window.alert(json.data?.message || 'Could not escalate.');
                return;
            }
            this.closeEscalateModal();
            this.fetchDetails(String(requestId));
        } catch (err) {
            console.error(err);
            this.setModalLoading(modal, false);
            this.setBtnLoading(confirmBtn, false);
            window.alert('Could not escalate.');
        }
    },

    async reportProblem(requestId, message = '') {
        const modal      = document.getElementById('report_problem_modal');
        const confirmBtn = document.getElementById('report_problem_confirm');
        const errorEl    = document.getElementById('report_problem_error');
        this.setModalLoading(modal, true);
        this.setBtnLoading(confirmBtn, true);
        try {
            const json = await apiClient.post('docuswift_report_problem', {
                request_id: String(requestId),
                message,
            });
            if (!json.success) {
                this.setModalLoading(modal, false);
                this.setBtnLoading(confirmBtn, false);
                const msg = json.data?.message || 'Could not report the problem.';
                if (errorEl) { errorEl.textContent = msg; errorEl.style.display = 'block'; }
                else window.alert(msg);
                return;
            }
            this.closeReportProblemModal();
            showToast('Problem reported. The request has been moved to Follow Up.', 'success');
            await this.fetchDetails(String(requestId));
        } catch (err) {
            console.error(err);
            this.setModalLoading(modal, false);
            this.setBtnLoading(confirmBtn, false);
            if (errorEl) { errorEl.textContent = 'Could not report the problem.'; errorEl.style.display = 'block'; }
            else window.alert('Could not report the problem.');
        }
    },

    async undoEscalateRequest(requestId) {
        const undoBtn = document.getElementById('undo_escalate_btn');
        this.setBtnLoading(undoBtn, true);
        try {
            const json = await apiClient.post('docuswift_undo_escalate_request', { request_id: String(requestId) });
            if (!json.success) {
                this.setBtnLoading(undoBtn, false);
                window.alert(json.data?.message || 'Could not undo escalation.');
                return;
            }
            this.fetchDetails(String(requestId));
        } catch (err) {
            console.error(err);
            this.setBtnLoading(undoBtn, false);
            window.alert('Could not undo escalation.');
        }
    },

    // ── Awaiting CM Review ────────────────────────────────────────────────────

    async setAwaitingCMReview(requestId, reason, note, reasonLabel, confirmBtn, errorEl) {
        if (confirmBtn) this.setBtnLoading(confirmBtn, true);
        const showError = (msg) => {
            if (errorEl) { errorEl.textContent = msg; errorEl.style.display = 'block'; }
            else window.alert(msg);
        };
        try {
            const json = await apiClient.post('docuswift_set_awaiting_cm_review', {
                request_id:              String(requestId),
                reason_for_case_manager: reason,
                reason_label:            reasonLabel,
                cm_reason_note:          note,
            });
            if (!json.success) {
                if (confirmBtn) this.setBtnLoading(confirmBtn, false);
                showError(json.data?.message || 'Could not set Awaiting CM Review.');
                return;
            }
            this.closeReasonForCaseManagerModal();
            this.refreshActivityLists(json.data || {});
            this.fetchDetails(String(requestId));
        } catch (err) {
            console.error(err);
            if (confirmBtn) this.setBtnLoading(confirmBtn, false);
            showError('Could not set Awaiting CM Review.');
        }
    },

    async undoAwaitingCMReview(requestId, triggerBtn, activityMessage = '') {
        if (triggerBtn) this.setBtnLoading(triggerBtn, true);
        const data = { request_id: String(requestId) };
        if (activityMessage) data.activity_message = activityMessage;
        try {
            const json = await apiClient.post('docuswift_unset_awaiting_cm_review', data);
            if (!json.success) {
                if (triggerBtn) this.setBtnLoading(triggerBtn, false);
                window.alert(json.data?.message || 'Could not undo Awaiting CM Review.');
                return;
            }
            this.refreshActivityLists(json.data || {});
            this.fetchDetails(String(requestId));
        } catch (err) {
            console.error(err);
            if (triggerBtn) this.setBtnLoading(triggerBtn, false);
            window.alert('Could not undo Awaiting CM Review.');
        }
    },

    // ── Ready for Payment ─────────────────────────────────────────────────────

    async setReadyForPayment(requestId, triggerBtn) {
        if (triggerBtn) this.setBtnLoading(triggerBtn, true);
        try {
            const json = await apiClient.post('docuswift_set_ready_for_payment', { request_id: String(requestId) });
            if (!json.success) {
                if (triggerBtn) this.setBtnLoading(triggerBtn, false);
                window.alert(json.data?.message || 'Could not set Ready for Payment.');
                return;
            }
            this.refreshActivityLists(json.data || {});
            this.fetchDetails(String(requestId));
        } catch (err) {
            console.error(err);
            if (triggerBtn) this.setBtnLoading(triggerBtn, false);
            window.alert('Could not set Ready for Payment.');
        }
    },

    async unsetReadyForPayment(requestId, triggerBtn, activityMessage = '') {
        if (triggerBtn) this.setBtnLoading(triggerBtn, true);
        const data = { request_id: String(requestId) };
        if (activityMessage) data.activity_message = activityMessage;
        try {
            const json = await apiClient.post('docuswift_unset_ready_for_payment', data);
            if (!json.success) {
                if (triggerBtn) this.setBtnLoading(triggerBtn, false);
                window.alert(json.data?.message || 'Could not undo Ready for Payment.');
                return;
            }
            this.refreshActivityLists(json.data || {});
            this.fetchDetails(String(requestId));
        } catch (err) {
            console.error(err);
            if (triggerBtn) this.setBtnLoading(triggerBtn, false);
            window.alert('Could not undo Ready for Payment.');
        }
    },

    // ── Ready for Download ────────────────────────────────────────────────────

    async setReadyForDownload(requestId, triggerBtn) {
        if (triggerBtn) this.setBtnLoading(triggerBtn, true);
        try {
            const json = await apiClient.post('docuswift_set_ready_for_download', { request_id: String(requestId) });
            if (!json.success) {
                if (triggerBtn) this.setBtnLoading(triggerBtn, false);
                window.alert(json.data?.message || 'Could not set Ready for Download.');
                return;
            }
            this.refreshActivityLists(json.data || {});
            this.fetchDetails(String(requestId));
        } catch (err) {
            console.error(err);
            if (triggerBtn) this.setBtnLoading(triggerBtn, false);
            window.alert('Could not set Ready for Download.');
        }
    },

    async unsetReadyForDownload(requestId, triggerBtn, activityMessage = '') {
        if (triggerBtn) this.setBtnLoading(triggerBtn, true);
        const data = { request_id: String(requestId) };
        if (activityMessage) data.activity_message = activityMessage;
        try {
            const json = await apiClient.post('docuswift_unset_ready_for_download', data);
            if (!json.success) {
                if (triggerBtn) this.setBtnLoading(triggerBtn, false);
                window.alert(json.data?.message || 'Could not undo Ready for Download.');
                return;
            }
            this.refreshActivityLists(json.data || {});
            this.fetchDetails(String(requestId));
        } catch (err) {
            console.error(err);
            if (triggerBtn) this.setBtnLoading(triggerBtn, false);
            window.alert('Could not undo Ready for Download.');
        }
    },

    // ── Follow-up date / sub-status ──────────────────────────────────────────

    updateFollowUpDate(requestId, followUpDate) {
        const formData = new URLSearchParams();
        formData.append('action', 'update_follow_up_date');
        formData.append('request_id', requestId);
        formData.append('follow_up_date', followUpDate);
        formData.append('nonce', window.docuswiftPortal?.nonce || '');
        fetch(this.ajaxUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: formData.toString(),
        })
            .then(res => res.json())
            .then(res => {
                if (res.success) {
                    if (res.data?.notes_html || res.data?.activity_html) {
                        this.refreshActivityLists(res.data);
                    } else {
                        this.fetchDetails(requestId);
                    }
                    this.syncPipelineFromResponse(res.data || {});
                } else {
                    console.error(res.data);
                }
            });
    },

    // ── Submitted / Resubmitted date ─────────────────────────────────────────

    _updateRequestDateField(action, fieldKey, requestId, dateVal) {
        const formData = new URLSearchParams();
        formData.append('action', action);
        formData.append('request_id', requestId);
        formData.append(fieldKey, dateVal);
        formData.append('nonce', window.docuswiftPortal?.nonce || '');
        fetch(this.ajaxUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: formData.toString(),
        })
            .then(res => res.json())
            .then(res => {
                if (res.success) {
                    if (res.data?.request_metrics) this.applyRequestMetrics(res.data.request_metrics);
                    if (res.data?.notes_html || res.data?.activity_html) this.refreshActivityLists(res.data);
                } else {
                    console.error(res.data);
                }
            });
    },

    updateRequestSubmittedDate(requestId, dateVal) {
        this._updateRequestDateField('update_request_submitted_date', 'request_submitted_date', requestId, dateVal);
    },

    updateRequestResubmittedDate(requestId, dateVal) {
        this._updateRequestDateField('update_request_resubmitted_date', 'request_resubmitted_date', requestId, dateVal);
    },

    updateRequestRecordType(requestId, recordTypeId, selectEl) {
        const formData = new URLSearchParams();
        formData.append('action', 'docuswift_update_request_record_type');
        formData.append('request_id', requestId);
        formData.append('record_type_id', recordTypeId);
        formData.append('nonce', window.docuswiftPortal?.nonce || '');

        // Show loading on the NiceSelect UI element (sits as next sibling of hidden select).
        const niceEl = selectEl?.nextElementSibling?.classList?.contains('nice-select')
            ? selectEl.nextElementSibling
            : selectEl?.parentElement?.querySelector('.nice-select');

        if (niceEl) niceEl.classList.add('disabled', 'ds-loading');

        const cleanup = () => {
            if (niceEl) niceEl.classList.remove('disabled', 'ds-loading');
        };

        fetch(this.ajaxUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: formData.toString(),
        })
            .then(res => res.json())
            .then(res => {
                if (res.success) {
                    this.fetchDetails(String(requestId));
                } else {
                    console.error('Record type update error:', res.data);
                    cleanup();
                }
            })
            .catch(err => {
                console.error('Record type update error:', err);
                cleanup();
            });
    },

    updateRequestAffidavit(requestId, value, selectEl) {
        const formData = new URLSearchParams();
        formData.append('action', 'docuswift_update_request_affidavit');
        formData.append('request_id', requestId);
        formData.append('affidavit', value);
        formData.append('nonce', window.docuswiftPortal?.nonce || '');

        const niceEl = selectEl?.nextElementSibling?.classList?.contains('nice-select')
            ? selectEl.nextElementSibling
            : selectEl?.parentElement?.querySelector('.nice-select');

        if (niceEl) niceEl.classList.add('disabled', 'ds-loading');

        const cleanup = () => {
            if (niceEl) niceEl.classList.remove('disabled', 'ds-loading');
        };

        fetch(this.ajaxUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: formData.toString(),
        })
            .then(res => res.json())
            .then(res => {
                if (res.success) {
                    this.fetchDetails(String(requestId));
                } else {
                    console.error('Affidavit update error:', res.data);
                    cleanup();
                }
            })
            .catch(err => {
                console.error('Affidavit update error:', err);
                cleanup();
            });
    },

    updateRequestSubStatus(requestId, subStatus, reasonForCaseManager = '', note = '', reasonLabel = '') {
        const formData = new URLSearchParams();
        formData.append('action', 'update_request_sub_status');
        formData.append('request_id', requestId);
        formData.append('sub_status', subStatus);
        formData.append('nonce', window.docuswiftPortal?.nonce || '');
        if (reasonForCaseManager) formData.append('reason_for_case_manager', reasonForCaseManager);
        if (reasonLabel)          formData.append('reason_for_case_manager_label', reasonLabel);
        if (note)                 formData.append('cm_reason_note', note);
        fetch(this.ajaxUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: formData.toString(),
        })
            .then((res) => res.json())
            .then((res) => {
                if (res.success) {
                    this.refreshActivityLists(res.data || {});
                } else {
                    const msg = res.data?.message || res.data || 'Could not update sub-status.';
                    showToast(msg, 'error', 6000);
                }
            })
            .catch((error) => {
                console.error('Sub-status update error:', error);
                showToast('Could not update sub-status.', 'error', 6000);
            });
    },

    resetInvoice(confirmBtn) {
        this.setBtnLoading(confirmBtn, true);

        const formData = new URLSearchParams();
        formData.append('action', 'docuswift_ajax_reset_request_invoice');
        formData.append('request_id', this.activeRequestId);
        formData.append('nonce', window.docuswiftPortal?.nonce || '');

        fetch(this.ajaxUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: formData.toString(),
        })
            .then(r => r.json())
            .then(res => {
                this.setBtnLoading(confirmBtn, false);
                if (!res.success) {
                    showToast(res.data?.message || 'Could not reset invoice.', 'error', 6000);
                    return;
                }
                this.closeResetInvoiceModal();
                showToast(res.data?.message || 'Invoice reset.', 'success', 4000);
                if (this.activeRequestId) {
                    this.fetchDetails(this.activeRequestId);
                }
            })
            .catch(err => {
                this.setBtnLoading(confirmBtn, false);
                console.error('Reset invoice error:', err);
                showToast('Could not reset invoice.', 'error', 6000);
            });
    },

    deleteInvoice(confirmBtn) {
        this.setBtnLoading(confirmBtn, true);

        const formData = new URLSearchParams();
        formData.append('action', 'docuswift_ajax_delete_request_invoice');
        formData.append('request_id', this.activeRequestId);
        formData.append('nonce', window.docuswiftPortal?.nonce || '');

        fetch(this.ajaxUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: formData.toString(),
        })
            .then(r => r.json())
            .then(res => {
                this.setBtnLoading(confirmBtn, false);
                if (!res.success) {
                    showToast(res.data?.message || 'Could not delete invoice.', 'error', 6000);
                    return;
                }
                this.closeDeleteInvoiceModal();
                showToast(res.data?.message || 'Invoice deleted.', 'success', 4000);
                if (this.activeRequestId) {
                    this.fetchDetails(this.activeRequestId);
                }
            })
            .catch(err => {
                this.setBtnLoading(confirmBtn, false);
                console.error('Delete invoice error:', err);
                showToast('Could not delete invoice.', 'error', 6000);
            });
    },

    syncQbPayment(btn) {
        this.setBtnLoading(btn, true);

        const formData = new URLSearchParams();
        formData.append('action', 'docuswift_ajax_sync_qb_payment');
        formData.append('request_id', this.activeRequestId);
        formData.append('nonce', window.docuswiftPortal?.nonce || '');

        fetch(this.ajaxUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: formData.toString(),
        })
            .then(r => r.json())
            .then(res => {
                this.setBtnLoading(btn, false);
                if (!res.success) {
                    showToast(res.data?.message || 'Could not sync payment.', 'error', 6000);
                    return;
                }
                const toastType = res.data?.status === 'marked_paid' ? 'success' : 'info';
                showToast(res.data?.message || 'Sync complete.', toastType, 5000);
                if (res.data?.status === 'marked_paid' && this.activeRequestId) {
                    this.fetchDetails(this.activeRequestId);
                }
            })
            .catch(err => {
                this.setBtnLoading(btn, false);
                console.error('Sync QB payment error:', err);
                showToast('Could not sync payment.', 'error', 6000);
            });
    },
};
